import React, { useMemo, useState } from "react"
import { Helmet } from "react-helmet-async"
import { Link } from "react-router-dom"
import { SEO_KEYWORDS_EXTRA } from "../seo/extraKeywords"

export default function Appointment() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    district: "",
    course: "B.Ed",
    preferredTime: "",
    message: "",
  })

  const whatsappLink = useMemo(() => {
    const base = "https://wa.me/918328830471"
    const text = [
      "Hi Devbhumi Educational Dreamzone,",
      "I want to book counselling.",
      `Name: ${form.name || "-"}`,
      `Phone: ${form.phone || "-"}`,
      `District: ${form.district || "-"}`,
      `Course: ${form.course || "-"}`,
      `Preferred time: ${form.preferredTime || "-"}`,
      form.message ? `Message: ${form.message}` : "",
      "Website: https://devbhumielearning.in/appointment",
    ]
      .filter(Boolean)
      .join("\n")

    return `${base}?text=${encodeURIComponent(text)}`
  }, [form])

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden max-w-[100vw]">
      <Helmet>
        <title>
          Book Counselling | B.Ed & BP.Ed Coaching Center Odisha | Devbhumi
        </title>
        <meta
          name="description"
          content="Book admission counselling for B.Ed/BP.Ed. Coaching center support for Odisha students (Bhadrak, Balasore, Baripada, Mayurbhanj, Karanjia). Online classes + office support."
        />
        <meta
          name="keywords"
          content={`B.Ed coaching center near me, BP.Ed coaching center near me, B.Ed private institute near me, BP.Ed institute near me, B.Ed admission counselling Odisha, BP.Ed coaching Odisha, counselling appointment Bhadrak, book B.Ed counselling online Odisha, ${SEO_KEYWORDS_EXTRA}`}
        />
        <link rel="canonical" href="https://devbhumielearning.in/appointment" />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Book Counselling | Devbhumi Educational Dreamzone"
        />
        <meta
          property="og:description"
          content="Book B.Ed/BP.Ed counselling + coaching support. Online classes and Odisha office support."
        />
        <meta
          property="og:url"
          content="https://devbhumielearning.in/appointment"
        />
      </Helmet>

      <main className="container mx-auto px-4 sm:px-6 py-10 sm:py-16 max-w-4xl min-w-0">
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6 min-w-0">
          <Link
            to="/"
            className="text-sm font-bold text-blue-700 hover:underline text-center sm:text-left shrink-0"
          >
            ← Back to Home
          </Link>
          <a
            href="tel:+918328830471"
            className="text-sm font-bold bg-blue-700 text-white px-4 py-3 sm:py-2 rounded-lg hover:bg-blue-800 text-center w-full sm:w-auto shrink-0"
          >
            Call Now
          </a>
        </div>

        <h1 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight mt-8 sm:mt-10 break-words">
          Book Counselling (B.Ed / BP.Ed)
        </h1>
        <p className="text-gray-600 mt-4 sm:mt-6 text-base sm:text-lg leading-relaxed break-words">
          If you searched “B.Ed / BP.Ed coaching center near me” or “private
          institute near me”, you can book counselling here. We support students
          across Odisha with online classes and offline office support in
          Bhadrak.
        </p>

        <div className="mt-8 sm:mt-10 grid md:grid-cols-2 gap-6 sm:gap-8 min-w-0">
          <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 sm:p-6 min-w-0">
            <h2 className="text-xl font-black">Quick booking (WhatsApp)</h2>
            <p className="text-gray-600 mt-3">
              Fill details and click WhatsApp. Your message will be ready to
              send.
            </p>

            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex mt-5 w-full sm:w-auto justify-center bg-green-600 hover:bg-green-700 text-white font-black px-5 py-3 rounded-xl text-center"
            >
              Book on WhatsApp
            </a>

            <div className="mt-6 text-sm text-gray-600 break-words">
              Office: Jagannathpur, near Bonth Chowk, opposite of Satsang Vihar
              Mandir, Bhadrak, Odisha 756100
            </div>
          </div>

          <form
            className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-6 shadow-sm min-w-0"
            onSubmit={(e) => {
              e.preventDefault()
              window.open(whatsappLink, "_blank", "noreferrer")
            }}
          >
            <h2 className="text-xl font-black">Your details</h2>

            <div className="mt-5 space-y-4">
              <input
                className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
              <input
                className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Phone number"
                value={form.phone}
                onChange={(e) =>
                  setForm((p) => ({ ...p, phone: e.target.value }))
                }
                required
              />
              <input
                className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="District (e.g., Bhadrak, Balasore, Cuttack...)"
                value={form.district}
                onChange={(e) =>
                  setForm((p) => ({ ...p, district: e.target.value }))
                }
              />
              <select
                className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.course}
                onChange={(e) =>
                  setForm((p) => ({ ...p, course: e.target.value }))
                }
              >
                <option value="B.Ed">B.Ed</option>
                <option value="BP.Ed">BP.Ed</option>
                <option value="B.Ed + BP.Ed">B.Ed + BP.Ed</option>
              </select>
              <input
                className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Preferred date/time"
                value={form.preferredTime}
                onChange={(e) =>
                  setForm((p) => ({ ...p, preferredTime: e.target.value }))
                }
              />
              <textarea
                className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Message (optional)"
                rows={4}
                value={form.message}
                onChange={(e) =>
                  setForm((p) => ({ ...p, message: e.target.value }))
                }
              />
              <button
                type="submit"
                className="w-full bg-blue-700 hover:bg-blue-800 text-white font-black py-3 rounded-xl"
              >
                Continue to WhatsApp
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}

